// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
export interface AppOptions {
  readonly meta: {
    readonly title: string;
    readonly name: string;
    readonly description: string;
    readonly social: {
      readonly twitter: string;
      readonly fb: string;
      readonly instagram: string;
      readonly tiktok: string;
      readonly youtube: string;
      readonly threads: string;
      readonly fbGroup: string;
    };
    readonly twitter: {
      readonly handle: string;
      readonly creatorHandle: string;
    };
  };
  readonly url: string;
  readonly reportURL?: string;
  readonly reportTimer?: number;
  readonly maintenance: boolean;
  readonly maintenanceDurationMinutes?: number;
  readonly maintenanceStartTime?: number;
  readonly confirmLimitMS: number;
  readonly debug: boolean;
  readonly prod: boolean;
  readonly googleClientId: string;
  readonly facebookAppId: string;
}
export type EnvironmentType = 'prod' | 'staging' | 'local';

// One week
export const ORDER_CREATION_BUFFER_SECONDS = 7 * 24 * 60 * 60;
export const TASK_CREATION_BUFFER_SECONDS = 7 * 24 * 60 * 60;

// Crop values
export const WEIGHT_UNITS = {
  GRAMS: 'grams' as const,
  OUNCES: 'ounces' as const,
};
export const CONVERTIBLE_WEIGHT_UNITS = {
  ...WEIGHT_UNITS,
  POUNDS: 'pounds' as const,
  KG: 'kg' as const,
};
export const CONVERTIBLE_WEIGHT_UNITS_VALUES = Object.values(CONVERTIBLE_WEIGHT_UNITS);
export const WEIGHT_UNITS_SHORT = {
  GRAMS: 'g',
  OUNCES: 'oz',
  POUNDS: 'lbs',
  KG: 'kg',
};
export const isValidWeightUnit = (unit: string): unit is WeightUnit =>
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  WEIGHT_UNITS_VALUES.includes(unit as any);
export const isValidConvertibleWeightUnit = (unit: string): unit is ConvertibleWeightUnit =>
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  CONVERTIBLE_WEIGHT_UNITS_VALUES.includes(unit as any);
export const TRAYS = 'trays';
export type WeightUnitPlusTrays = 'grams' | 'ounces' | 'trays';
export const isValidWeightUnitPlusTrays = (unit: string): unit is WeightUnitPlusTrays =>
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  WEIGHT_UNITS_VALUES.includes(unit as any) || TRAYS === unit;
export const getWeightUnitOptions = (preferredUnit: WeightUnit): WeightUnitPlusTrays[] => {
  return preferredUnit === WEIGHT_UNITS.OUNCES
    ? [WEIGHT_UNITS.OUNCES, WEIGHT_UNITS.GRAMS]
    : [WEIGHT_UNITS.GRAMS, WEIGHT_UNITS.OUNCES];
};
export type ConvertibleWeightUnit = 'pounds' | 'kg' | 'grams' | 'ounces';
export type WeightUnit = 'grams' | 'ounces';
export const WEIGHT_UNITS_VALUES = Object.values(WEIGHT_UNITS);
export const WEIGHT_UNIT_PLUS_TRAYS_VALUES = [...WEIGHT_UNITS_VALUES, TRAYS];
export const SOW_DENSITY_UNITS = {
  GRAMS: 'grams',
  OUNCES: 'ounces',
  TEASPOONS: 'teaspoons',
  TABLESPOONS: 'tablespoons',
  CUPS: 'cups',
};
export const SOW_DENSITY_UNITS_VALUES = Object.values(SOW_DENSITY_UNITS);

// Order values
export const ORDER_INPUT_TYPES = [WEIGHT_UNITS.OUNCES, WEIGHT_UNITS.GRAMS, TRAYS];
export const ORDER_STATUS = {
  PLANNED: 'planned',
  IN_PROGRESS: 'in_progress',
  CANCELLED: 'cancelled',
  COMPLETED: 'completed',
};
export const ORDER_STATUS_VALUES = Object.values(ORDER_STATUS);
export const MAX_ORDER_WEEKLY_FREQUENCY = 12;
export const MAX_TASK_WEEKLY_FREQUENCY = 52;
export const RECURRING_FREQUENCY_UNITS = {
  DAYS: 'days',
  WEEKS: 'weeks',
  DAY_OF_WEEK: 'dayOfWeek',
  DAY_OF_MONTH: 'dayOfMonth',
};
export const RECURRING_FREQUENCY_UNITS_VALUES = Object.values(RECURRING_FREQUENCY_UNITS);

// Planting values
export const PLANTING_STATUS = {
  PLANNED: 'planned',
  IN_PROGRESS: 'in_progress',
  // CANCELLED: 'cancelled',
  COMPLETED: 'completed',
};
export const PLANTING_STATUS_VALUES = Object.values(PLANTING_STATUS);
export const PLANTING_STATE = {
  ALLOCATED: 'allocated',
  SURPLUS: 'surplus',
  BUFFER: 'buffer',
};
export const PLANTING_STATE_VALUES = Object.values(PLANTING_STATE);

// Task values
export const TASK_STATUS = {
  PLANNED: 'planned',
  COMPLETED: 'completed',
};
export const TASK_STATUS_VALUES = Object.values(TASK_STATUS);

export const STEP_NAME = {
  SOAK: 'Soak',
  PLANT: 'Plant',
  WEIGHTED_GERMINATION: 'Weighted germination',
  UNWEIGHTED_GERMINATION: 'Unweighted germination',
  WATERING: 'Watering',
  BLACKOUT: 'Blackout',
  HUMIDITY_DOME: 'Humidity dome',
  UNDER_LIGHTS: 'Under lights',
  HARVEST: 'Harvest',
};
// Lower number means higher priority, meaning should come before other steps
export const STEP_NAME_TO_PRIORITY = {
  [STEP_NAME.SOAK]: 10,
  [STEP_NAME.PLANT]: 8,
  [STEP_NAME.WEIGHTED_GERMINATION]: 7,
  [STEP_NAME.UNWEIGHTED_GERMINATION]: 6,
  [STEP_NAME.WATERING]: 5,
  [STEP_NAME.BLACKOUT]: 4,
  [STEP_NAME.HUMIDITY_DOME]: 3,
  [STEP_NAME.UNDER_LIGHTS]: 2,
  [STEP_NAME.HARVEST]: 1,
};
const GERMINATION_STEPS = new Set([
  STEP_NAME.WEIGHTED_GERMINATION,
  STEP_NAME.UNWEIGHTED_GERMINATION,
  STEP_NAME.BLACKOUT,
  STEP_NAME.HUMIDITY_DOME,
]);
export const isGerminationStep = (stepName: string) => GERMINATION_STEPS.has(stepName);
const DURATION_STEPS = new Set([
  STEP_NAME.WEIGHTED_GERMINATION,
  STEP_NAME.UNWEIGHTED_GERMINATION,
  STEP_NAME.BLACKOUT,
  STEP_NAME.HUMIDITY_DOME,
  STEP_NAME.UNDER_LIGHTS,
]);
export const isDurationStep = (stepName: string) => DURATION_STEPS.has(stepName);
export const cropStepNameToShortName = (name: string) => {
  switch (name) {
    case STEP_NAME.WEIGHTED_GERMINATION:
      return 'Germinate';
    case STEP_NAME.UNWEIGHTED_GERMINATION:
      return 'Germinate';
    case STEP_NAME.BLACKOUT:
      return 'Blackout';
    case STEP_NAME.HARVEST:
      return 'Harvest';
    case STEP_NAME.HUMIDITY_DOME:
      return 'Dome';
    case STEP_NAME.UNDER_LIGHTS:
      return 'Lights';
    case STEP_NAME.WATERING:
      return 'Water';
    case STEP_NAME.PLANT:
      return 'Plant';
    default:
      return name;
  }
};
export const STEP_NAME_VALUES = Object.values(STEP_NAME);

// Recurring Order values
export const RECURRING_ORDER_STATUS = {
  ACTIVE: 'active',
  PAUSED: 'paused',
};
export const RECURRING_ORDER_STATUS_VALUES = Object.values(RECURRING_ORDER_STATUS);
export const RECURRING_TASK_STATUS = {
  ACTIVE: 'active',
  PAUSED: 'paused',
};
export const RECURRING_TASK_STATUS_VALUES = Object.values(RECURRING_TASK_STATUS);

// Crop status
export const CROP_STATUS = {
  ACTIVE: 'active',
  ARCHIVED: 'archived',
};
export const CROP_STATUS_VALUES = Object.values(CROP_STATUS);

// Blend status
export const BLEND_STATUS = {
  ACTIVE: 'active',
  ARCHIVED: 'archived',
};
export const BLEND_STATUS_VALUES = Object.values(BLEND_STATUS);

// Permissions
export const PERMISSION_TYPE = {
  CAN_EDIT_ORDERS: 'can_edit_orders',
  CAN_EDIT_CROPS_BLENDS: 'can_edit_crops_blends',
  CAN_EDIT_TASKS: 'can_edit_tasks',
  CAN_COMPLETE_TASKS: 'can_complete_tasks',
};
export const PERMISSION_TYPE_VALUES = Object.values(PERMISSION_TYPE);

// User invites
export const USER_INVITE_STATUS = {
  PENDING: 'pending',
  CANCELLED: 'cancelled',
};
export const USER_INVITE_STATUS_VALUES = Object.values(USER_INVITE_STATUS);
export const USER_INVITE_TOKEN_PARAM = 'inviteToken';

export const AFFILIATE_PARAM = 'aff';
export const FIRSTNAME_PARAM = 'firstname';
export const LASTNAME_PARAM = 'lastname';
export const FARMNAME_PARAM = 'farmname';
export const EMAIL_PARAM = 'email';
export const PASSWORD_RESET_TOKEN_PARAM = 'resetToken';

export const URL_PARAMS = [
  AFFILIATE_PARAM,
  FIRSTNAME_PARAM,
  LASTNAME_PARAM,
  FARMNAME_PARAM,
  EMAIL_PARAM,
  USER_INVITE_TOKEN_PARAM,
];

export const LOGGED_OUT_ERROR_MESSAGE =
  'You need to be logged in to view this. Your session may have expired or you may have logged out.';
